import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { IoSearch } from '@react-icons/all-files/io5/IoSearch'
import { IconButton, TextField } from '@radix-ui/themes'
import React from 'react'
import styles from './index.module.css'

type Props = React.ComponentProps<typeof TextField.Root> & {
  nodeLeft?: React.ReactNode
  nodeRight?: React.ReactNode
  iconPosition?: 'left' | 'right'
  theme?: 'light' | 'dark'
  onClear?: () => void
}

/**
 * Search input field with search icon and clear button functionality.
 *
 * Extends Radix UI TextField with built-in search UX patterns and value clearing.
 * Includes a search icon (configurable position) and a clear button on the right when there's a value.
 *
 * @param onChange - Callback function when the input value changes
 * @param placeholder - Placeholder text for the input (default: 'Search...')
 * @param type - Input type (default: 'search')
 * @param iconPosition - Position of search icon: 'left' or 'right' (default: 'left')
 * @param nodeLeft - Custom content for the left slot (replaces search icon when provided)
 * @param nodeRight - Optional custom node for the right side (coexists with clear button)
 * @param rest - All other props passed to the underlying TextField component
 * @see https://www.radix-ui.com/themes/docs/components/text-field - Radix UI TextField documentation
 */
// SLAM-1939: Use React.JSX for React 19 compatibility.
export const SearchTextField = ({
  onChange,
  placeholder = 'Search...',
  type = 'search',
  iconPosition = 'left',
  theme = 'light',
  onClear,
  nodeLeft,
  nodeRight,
  ...rest
}: Props): React.JSX.Element => {
  const handleClear = () => {
    if (onChange) {
      onChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>)
    }
    if (onClear) {
      onClear()
    }
  }

  return (
    <TextField.Root
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      aria-label="Search"
      className={theme === 'dark' ? styles['SearchTextField--dark'] : undefined}
      {...rest}
    >
      {rest.value && (
        <TextField.Slot side="right">
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            className={styles.clearIcon}
            onClick={handleClear}
          >
            <IoClose />
          </IconButton>
        </TextField.Slot>
      )}
      {nodeRight && <TextField.Slot side="right">{nodeRight}</TextField.Slot>}
      {nodeLeft ? (
        <TextField.Slot side="left">{nodeLeft}</TextField.Slot>
      ) : (
        <TextField.Slot side={iconPosition}>
          <IoSearch aria-hidden />
        </TextField.Slot>
      )}
    </TextField.Root>
  )
}
